import { useCallback, useMemo, useState } from 'react';

import useMonolithApiQuery from '@/hooks/api/useMonolithApiQuery';
import useQueryParams from '@/hooks/useQueryParams';
import { BuildCacheToolIds } from '@/models/BuildCacheTool';
import { BuildToolInvocationStatus, BuildToolInvocationWithDetails } from '@/models/BuildToolInvocation';
import { CiProviderId } from '@/models/CiProvider';
import { SimpleErrorResponse } from '@/models/Error';
import { parseSortBy } from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/enums/invocationListTable';
import { buildCacheInvocationListPath } from '@/utils/monolithApiRouteService';
import { updateQueryString } from '@/utils/queryParams';

/** Each filter's selected values, keyed by filter. */
export type InvocationFilterState = Record<string, string[]>;

export enum PageQueryParamKeys {
  page = 'page',
  perPage = 'items_per_page',
  sortBy = 'order_by',
  sortDirection = 'order_direction',
  projectSlug = 'project_slug',
  repositoryUrl = 'repository_url',
  workflow = 'workflow',
  command = 'command',
  username = 'username',
  status = 'status',
  tool = 'tool',
  ciProvider = 'ci_provider',
  dateFrom = 'after',
  dateTo = 'before',
}

enum QueryParamKeys {
  page = 'page',
  perPage = 'items_per_page',
  sortBy = 'order_by',
  sortDirection = 'order_direction',
  projectSlug = 'project_slug',
  repositoryUrl = 'repository_url',
  workflow = 'workflow',
  command = 'command',
  username = 'username',
  status = 'status',
  tool = 'tool',
  ciProvider = 'ci_provider',
  dateFrom = 'after',
  dateTo = 'before',
}

// Slow enough not to multiply the list endpoint's Insights enrichment, quick enough that a finished invocation
// does not sit there claiming to be running for long.
const RUNNING_INVOCATION_POLL_MS = 30_000;

export type InvocationListResponse = {
  items: BuildToolInvocationWithDetails[];
  paging: {
    page: number;
    perPage: number;
    totalCount: number;
  };
};

const InvocationFilterKeys = [
  QueryParamKeys.projectSlug,
  QueryParamKeys.repositoryUrl,
  QueryParamKeys.ciProvider,
  QueryParamKeys.workflow,
  QueryParamKeys.command,
  QueryParamKeys.username,
  QueryParamKeys.status,
  QueryParamKeys.dateTo,
  QueryParamKeys.dateFrom,
  QueryParamKeys.page,
];

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const useInvocations = (
  workspaceSlug: string,
  isEnabled?: boolean,
  forcedProjectSlug?: string,
  preSelectedTool?: BuildCacheToolIds,
) => {
  const queryParams = useQueryParams();

  const [defaultDateRange] = useState<[string, string]>(() => {
    const now = Date.now();
    return [String(now - THIRTY_DAYS_MS), String(now)];
  });

  const userProjectSlug = forcedProjectSlug ? null : queryParams.get(PageQueryParamKeys.projectSlug);
  const userRepositoryUrl = forcedProjectSlug ? null : queryParams.get(PageQueryParamKeys.repositoryUrl);
  const requestProjectSlug = forcedProjectSlug ?? userProjectSlug;
  const workflowName = queryParams.get(PageQueryParamKeys.workflow);
  const command = queryParams.get(PageQueryParamKeys.command);
  const username = queryParams.get(PageQueryParamKeys.username);
  const status = queryParams.get(PageQueryParamKeys.status) as `${BuildToolInvocationStatus}`;
  const toolFromQuery = queryParams.get(PageQueryParamKeys.tool);
  const validToolFromQuery = Object.values(BuildCacheToolIds).includes(toolFromQuery as BuildCacheToolIds)
    ? (toolFromQuery as BuildCacheToolIds)
    : null;
  const tool = validToolFromQuery ?? preSelectedTool ?? BuildCacheToolIds.gradle;
  const ciProvider = queryParams.get(PageQueryParamKeys.ciProvider) as `${CiProviderId}`;
  const dateFrom = queryParams.get(PageQueryParamKeys.dateFrom);
  const dateTo = queryParams.get(PageQueryParamKeys.dateTo);

  const searchParams = useMemo(() => {
    const page = queryParams.get(PageQueryParamKeys.page);
    const perPage = queryParams.get(PageQueryParamKeys.perPage);
    const sortBy = parseSortBy(queryParams.get(PageQueryParamKeys.sortBy));
    const sortDirection = sortBy ? queryParams.get(PageQueryParamKeys.sortDirection) : null;

    const urlSearchParams = new URLSearchParams();

    if (requestProjectSlug) {
      urlSearchParams.append(QueryParamKeys.projectSlug, requestProjectSlug);
    }

    if (userRepositoryUrl) {
      urlSearchParams.append(QueryParamKeys.repositoryUrl, userRepositoryUrl);
    }

    if (workflowName) {
      urlSearchParams.append(QueryParamKeys.workflow, workflowName);
    }

    if (command) {
      urlSearchParams.append(QueryParamKeys.command, command);
    }

    if (username) {
      urlSearchParams.append(QueryParamKeys.username, username);
    }

    if (status) {
      urlSearchParams.append(QueryParamKeys.status, status);
    }

    urlSearchParams.append(QueryParamKeys.tool, tool);

    if (ciProvider) {
      urlSearchParams.append(QueryParamKeys.ciProvider, ciProvider);
    }

    if (dateFrom && dateTo) {
      urlSearchParams.append(QueryParamKeys.dateFrom, dateFrom);
      urlSearchParams.append(QueryParamKeys.dateTo, dateTo);
    }

    if (page) {
      urlSearchParams.append(QueryParamKeys.page, page);
    }

    if (perPage) {
      urlSearchParams.append(QueryParamKeys.perPage, perPage);
    }

    if (sortBy) {
      urlSearchParams.append(QueryParamKeys.sortBy, sortBy);
    }

    if (sortDirection) {
      urlSearchParams.append(QueryParamKeys.sortDirection, sortDirection);
    }

    return urlSearchParams.toString();
  }, [
    queryParams,
    requestProjectSlug,
    userRepositoryUrl,
    workflowName,
    command,
    username,
    status,
    tool,
    ciProvider,
    dateFrom,
    dateTo,
  ]);

  const url = useMemo(() => {
    return buildCacheInvocationListPath(workspaceSlug, { format: 'json', params: searchParams });
  }, [searchParams, workspaceSlug]);

  const filterState = useMemo(() => {
    const filters: InvocationFilterState = {};

    if (userProjectSlug) {
      filters.project = [userProjectSlug];
    }

    if (!userProjectSlug && userRepositoryUrl) {
      filters.project = [userRepositoryUrl];
    }

    if (status) {
      filters.status = [status];
    }

    if (workflowName) {
      filters.workflow = [workflowName];
    }

    if (command) {
      filters.command = [command];
    }

    if (username) {
      filters.username = [username];
    }

    if (ciProvider) {
      filters.ci_provider = [ciProvider];
    }

    filters.date_range = dateFrom && dateTo ? [dateFrom, dateTo] : defaultDateRange;

    return filters;
  }, [
    userProjectSlug,
    ciProvider,
    dateFrom,
    dateTo,
    userRepositoryUrl,
    status,
    workflowName,
    command,
    username,
    defaultDateRange,
  ]);

  const isFiltered = Boolean(
    userProjectSlug ||
    userRepositoryUrl ||
    workflowName ||
    command ||
    username ||
    status ||
    ciProvider ||
    (dateFrom && dateTo),
  );

  const onFilterChange = useCallback(
    (filter: PageQueryParamKeys, filterValue: string | null) => {
      if (filterValue) {
        queryParams.set(filter, filterValue);
      } else {
        queryParams.delete(filter);
      }

      queryParams.delete(PageQueryParamKeys.page);
      updateQueryString(queryParams);
    },
    [queryParams],
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      queryParams.set(PageQueryParamKeys.page, newPage.toString());
      updateQueryString(queryParams);
    },
    [queryParams],
  );

  const onPerPageChange = useCallback(
    (newPerPage: number) => {
      queryParams.set(PageQueryParamKeys.perPage, newPerPage.toString());
      queryParams.set(PageQueryParamKeys.page, '');
      updateQueryString(queryParams);
    },
    [queryParams],
  );

  const onFilterClear = useCallback(() => {
    InvocationFilterKeys.forEach((filterItem) => {
      queryParams.delete(filterItem);
    });
    updateQueryString(queryParams);
  }, [queryParams]);

  return {
    ...useMonolithApiQuery<InvocationListResponse, SimpleErrorResponse>(['buildCacheInvocationList', url], url, {
      enabled: isEnabled,
      // A running invocation renders a live count-up timer and an "In progress" cache status, and nothing else
      // brings the page back to the API (refetchOnWindowFocus is off globally), so without this the timer would
      // keep ticking long after the invocation finished. `state.data` is still the axios envelope here — the
      // `select` that unwraps it runs after.
      refetchInterval: (query) =>
        query.state.data?.data.items.some((item) => item.status === BuildToolInvocationStatus.pending)
          ? RUNNING_INVOCATION_POLL_MS
          : false,
    }),
    selectedTool: tool,
    filterState,
    defaultDateRange,
    isFiltered,
    onFilterChange,
    onPageChange,
    onPerPageChange,
    onFilterClear,
  };
};

export default useInvocations;
